import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Text } from '@/ui/core/typography'
import { BlurView } from 'expo-blur'
import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native'
import type { BaseToastProps } from 'react-native-toast-message'

/**
 * A toast is a note handed to you, not a banner across the app: a floating slab of dark glass
 * near the foot of the screen, inset from both edges and lit along its top rim. The chrome stays
 * neutral so one object can carry anything the app has to say — saved, failed, downloaded, sent —
 * and the colour lives in the single glyph, which is what tells a success from a failure at a
 * glance. It slides up on arrival and drops back down on the way out.
 */
type Tone = { disc: string; glyph: GlyphName }

/** Three soft discs against the glass. Anything louder fights the message. */
const TONES: Record<'success' | 'error' | 'info', Tone> = {
	success: { disc: colors.mint, glyph: 'check' },
	error: { disc: colors.ground, glyph: 'x' },
	info: { disc: colors.white, glyph: 'asterisk' },
}

/**
 * Glass needs something behind it, and Android's blur is a pale imitation, so the scrim carries
 * the contrast there and merely deepens it on iOS.
 */
const SCRIM = Platform.select({
	ios: 'rgba(32,26,26,0.58)',
	default: 'rgba(32,26,26,0.96)',
}) as string

/** Room for two lines whether or not the message needs them; one row reads thin and provisional. */
const MIN_HEIGHT = 72
/** Inset from both edges on a phone; no wider than a column on anything larger. */
const SIDE_INSET = 16
const MAX_WIDTH = 560

const Toast = ({ text1, text2, onPress, tone }: BaseToastProps & { tone: Tone }) => {
	// Half the app's toasts carry only a detail line. Whichever line arrives is the message.
	const message = text1 || text2

	// The library hands onPress to the renderer rather than wiring it itself, so a toast that
	// leads somewhere — Settings, a screen — is only tappable if we make it so.
	const Frame = onPress ? Pressable : View

	// The library centres the toast and sizes it to its content, so without a width of its own
	// the row would shrink to the disc and leave the message no room at all.
	const { width: screenWidth } = useWindowDimensions()
	const width = Math.min(screenWidth - SIDE_INSET * 2, MAX_WIDTH)

	return (
		<Frame
			onPress={onPress}
			accessibilityRole={onPress ? 'button' : undefined}
			className="overflow-hidden rounded-note"
			style={{ width, boxShadow: '0 12px 32px rgba(40,20,18,0.42)' }}
		>
			<BlurView
				intensity={Platform.OS === 'ios' ? 60 : 0}
				tint="dark"
				style={StyleSheet.absoluteFill}
			/>
			<View style={[StyleSheet.absoluteFill, { backgroundColor: SCRIM }]} />
			{/* The rim: glass catches the light along its edge. */}
			<View
				pointerEvents="none"
				style={[
					StyleSheet.absoluteFill,
					{
						borderWidth: StyleSheet.hairlineWidth,
						borderColor: 'rgba(255,255,255,0.16)',
						borderRadius: 24,
					},
				]}
			/>
			<View
				className="flex-row items-center px-4 py-4"
				style={{ gap: 12, minHeight: MIN_HEIGHT }}
			>
				<View
					className="h-7 w-7 shrink-0 items-center justify-center rounded-full"
					style={{ backgroundColor: tone.disc }}
				>
					<Glyph name={tone.glyph} size={13} color={colors.ink} />
				</View>
				<Text
					size="body1"
					numberOfLines={2}
					className="flex-1 font-body-medium"
					style={{ color: colors.white }}
				>
					{message}
				</Text>
			</View>
		</Frame>
	)
}

const toastConfig = {
	success: (props: BaseToastProps) => <Toast {...props} tone={TONES.success} />,
	error: (props: BaseToastProps) => <Toast {...props} tone={TONES.error} />,
	info: (props: BaseToastProps) => <Toast {...props} tone={TONES.info} />,
}

export default toastConfig
